import { LanguageRounded, MenuRounded } from "@mui/icons-material";
import { Box, Button, IconButton, Link, Typography, Stack, Menu, MenuItem, Tooltip, Fade } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useIsMobile from "../hooks/useIsMobile";
import { Language2EndpointPostPromise } from "../routes/Language";
import { theme } from "./theme";
import useTranslationState from "./useTranslationState";

function Nav3() {
    const navigate = useNavigate();

    const translations = useTranslationState({
        home: 'Home',
        privateChefs: 'Private Chefs',
        rentalChefs: 'Rental Chefs',
        consulting: 'Consulting',
        changeLanguage: 'Change Language',
        userLanguageSetting: '',
    });
    const isMobile = useIsMobile();
    const [menuOpen, setMenuOpen] = useState(false);
    
    // Language menu state
    const [language, setLanguage] = useState('');
    const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
    const languageMenuOpen = Boolean(languageMenuAnchor);

    useEffect(() => {
        if (translations.userLanguageSetting !== '') {
            setLanguage(translations.userLanguageSetting);
        }
    }, [translations]);

    useEffect(() => {
        if (language === '' || language === translations.userLanguageSetting) {
            return;
        }

        const promise = Language2EndpointPostPromise(language);

        promise.then(() => {
            navigate(0);
        });
    }, [language]);

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    const navigateTo = (path: string) => {
        navigate(path);
        setMenuOpen(false);
    };
    
    const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setLanguageMenuAnchor(event.currentTarget);
    };
    
    const handleLanguageMenuClose = () => {
        setLanguageMenuAnchor(null);
    };
    
    const handleLanguageChange = (lang: string) => {
        setLanguage(lang);
        setLanguageMenuAnchor(null);
    };

    const navLinks = (
        <Stack
            direction={isMobile ? "column" : "row"}
            spacing={isMobile ? 4 : 5}
            alignItems="center"
            sx={{
                width: '100%',
                justifyContent: isMobile ? 'center' : 'flex-end',
            }}
        >
            <Link onClick={() => navigateTo('/')} sx={{ color: 'white', textDecoration: 'none', cursor: 'pointer', fontSize: 16 }}>
                {translations.home}
            </Link>
            <Link onClick={() => navigateTo('/private-chefs')} sx={{ color: 'white', textDecoration: 'none', cursor: 'pointer', fontSize: 16 }}>
                {translations.privateChefs}
            </Link>
            <Link onClick={() => navigateTo('/rental-chefs')} sx={{ color: 'white', textDecoration: 'none', cursor: 'pointer', fontSize: 16 }}>
                {translations.rentalChefs}
            </Link>
            <Link onClick={() => navigateTo('/consulting')} sx={{ color: 'white', textDecoration: 'none', cursor: 'pointer', fontSize: 16 }}>
                {translations.consulting}
            </Link>
            
            <Tooltip title={translations.changeLanguage}>
                <IconButton 
                    onClick={handleLanguageMenuOpen} 
                    size="small" 
                    sx={{ 
                        color: 'white', 
                        ml: isMobile ? 0 : 1,
                        border: '1px solid rgba(255,255,255,0.3)',
                        padding: '8px',
                        transition: 'all 0.2s',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            borderColor: 'rgba(255,255,255,0.5)',
                        }
                    }}
                >
                    <LanguageRounded fontSize="small" />
                </IconButton>
            </Tooltip>
            
            <Menu
                anchorEl={languageMenuAnchor}
                open={languageMenuOpen}
                onClose={handleLanguageMenuClose}
                TransitionComponent={Fade}
                PaperProps={{
                    elevation: 2,
                    sx: {
                        borderRadius: '12px',
                        minWidth: 180,
                        overflow: 'visible',
                        mt: 1.5,
                        backgroundColor: theme.palette.primary.dark,
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.1)',
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: theme.palette.primary.dark,
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                            borderLeft: '1px solid rgba(255,255,255,0.1)',
                            borderTop: '1px solid rgba(255,255,255,0.1)',
                        },
                    }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={() => handleLanguageChange('english')} sx={{ fontSize: '0.9rem', py: 1 }}>
                    English 🇺🇸
                </MenuItem>
                <MenuItem onClick={() => handleLanguageChange('german')} sx={{ fontSize: '0.9rem', py: 1 }}>
                    Deutsch 🇩🇪
                </MenuItem>
                <MenuItem onClick={() => handleLanguageChange('french')} sx={{ fontSize: '0.9rem', py: 1 }}>
                    Français 🇫🇷
                </MenuItem>
                <MenuItem onClick={() => handleLanguageChange('spanish')} sx={{ fontSize: '0.9rem', py: 1 }}>
                    Español 🇪🇸
                </MenuItem>
                <MenuItem onClick={() => handleLanguageChange('italian')} sx={{ fontSize: '0.9rem', py: 1 }}>
                    Italiano 🇮🇹
                </MenuItem>
                <MenuItem onClick={() => handleLanguageChange('dutch')} sx={{ fontSize: '0.9rem', py: 1 }}>
                    Nederlands 🇳🇱
                </MenuItem>
                <MenuItem onClick={() => handleLanguageChange('portuguese')} sx={{ fontSize: '0.9rem', py: 1 }}>
                    Português 🇵🇹
                </MenuItem>
            </Menu>
        </Stack>
    );

    return (
        <>
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: 80,
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    zIndex: 100,
                    px: isMobile ? 2 : 6,
                    boxSizing: 'border-box',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                }}
            >
                <img
                    src="logo.png"
                    alt="Ukoni"
                    style={{
                        height: '120%',
                        width: 'auto',
                        filter: 'invert(1)',
                        cursor: 'pointer',
                    }}
                    onClick={() => navigate('/')}
                />
                {isMobile ? (
                    <IconButton onClick={toggleMenu}>
                        <MenuRounded sx={{ color: 'white' }} />
                    </IconButton>
                ) : (
                    navLinks
                )}
            </Box>
            {isMobile && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 80,
                        left: 0,
                        width: '100%',
                        height: 'calc(100vh - 80px)',
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        backdropFilter: 'blur(10px)',
                        zIndex: 99,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transform: menuOpen ? 'translateY(0)' : 'translateY(-100%)',
                        transition: 'transform 0.3s ease-in-out',
                        pt: 8,
                    }}
                >
                    {navLinks}
                </Box>
            )}
        </>
    );
}

export default Nav3; 